import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";

/* ── data ── */
const DESTINATIONS = [
  {
    id: 1, from: "Antananarivo", to: "Toamasina",
    distance: "370 km", duration: "4h", price: 38000,
    type: "Bus", trips: 24, rating: 4.7,
    desc: "La route de l'Est, entre hautes terres et côte atlantique.",
    tags: ["Populaire", "Fréquent"],
    color: "#f0fdf4", accent: "#16a34a",
  },
  {
    id: 2, from: "Antananarivo", to: "Fianarantsoa",
    distance: "403 km", duration: "5h30", price: 45000,
    type: "Bus", trips: 18, rating: 4.6,
    desc: "Traversée des hauts plateaux verdoyants vers la capitale du vin malgache.",
    tags: ["Paysages"],
    color: "#eff6ff", accent: "#3b82f6",
  },
  {
    id: 3, from: "Antananarivo", to: "Mahajanga",
    distance: "571 km", duration: "8h", price: 70000,
    type: "Taxi-brousse", trips: 10, rating: 4.4,
    desc: "Cap vers le Nord-Ouest et les plages de l'océan Indien.",
    tags: ["Longue distance"],
    color: "#fffbeb", accent: "#d97706",
  },
  {
    id: 4, from: "Antananarivo", to: "Antsirabe",
    distance: "170 km", duration: "2h30", price: 20000,
    type: "Bus", trips: 32, rating: 4.8,
    desc: "La ville thermale, première grande étape sur la route du Sud.",
    tags: ["Court trajet", "Fréquent"],
    color: "#fdf4ff", accent: "#9333ea",
  },
  {
    id: 5, from: "Toamasina", to: "Fénérive-Est",
    distance: "180 km", duration: "3h", price: 22000,
    type: "Taxi-brousse", trips: 8, rating: 4.3,
    desc: "Le long de la côte Est, entre cocotiers et canaux.",
    tags: ["Côtier"],
    color: "#fff7ed", accent: "#ea580c",
  },
  {
    id: 6, from: "Fianarantsoa", to: "Toliara",
    distance: "643 km", duration: "9h", price: 80000,
    type: "Bus", trips: 6, rating: 4.5,
    desc: "Descente vers le Grand Sud aride et ses paysages lunaires.",
    tags: ["Grand Sud", "Aventure"],
    color: "#f0fdf4", accent: "#16a34a",
  },
  {
    id: 7, from: "Antananarivo", to: "Ambositra",
    distance: "252 km", duration: "4h", price: 28000,
    type: "Bus", trips: 12, rating: 4.5,
    desc: "La capitale de l'artisanat malgache et de la marqueterie.",
    tags: ["Artisanat"],
    color: "#eff6ff", accent: "#3b82f6",
  },
  {
    id: 8, from: "Mahajanga", to: "Antsiranana",
    distance: "692 km", duration: "10h", price: 90000,
    type: "Taxi-brousse", trips: 4, rating: 4.2,
    desc: "Le Grand Nord, entre baobabs et mer d'émeraude.",
    tags: ["Nord", "Longue distance"],
    color: "#fffbeb", accent: "#d97706",
  },
  {
    id: 9, from: "Antananarivo", to: "Moramanga",
    distance: "113 km", duration: "1h45", price: 15000,
    type: "Bus", trips: 28, rating: 4.9,
    desc: "Porte d'entrée vers le parc national de l'Andasibe.",
    tags: ["Nature", "Court trajet"],
    color: "#f0fdf4", accent: "#16a34a",
  },
];

const ALL_TYPES  = ["Tous", "Bus", "Taxi-brousse"];
const ALL_CITIES = ["Toutes", "Antananarivo", "Toamasina", "Fianarantsoa", "Mahajanga", "Antsirabe", "Moramanga", "Ambositra", "Toliara", "Antsiranana"];

/* ── sub-components ── */
const Header = () => (
  <header style={s.header}>
    <Link to="/" style={s.logo}>Trans<span style={{ color: "#16a34a" }}>TiGo</span></Link>
    <nav style={s.nav}>
      <Link to="/destinations" style={{ ...s.navLink, color: "#1a1a1a", fontWeight: 600 }}>Destinations</Link>
      <Link to="/covoiturage"  style={s.navLink}>Covoiturage</Link>
      <Link to="/tarifs"       style={s.navLink}>Tarifs</Link>
      <Link to="/contact"      style={s.navLink}>Contact</Link>
    </nav>
    <div style={{ display: "flex", gap: 10 }}>
      <Link to="/login"    style={s.btnGhost}>Connexion</Link>
      <Link to="/register" style={s.btnPrimary}>S'inscrire</Link>
    </div>
  </header>
);

const Tag = ({ label, accent }) => (
  <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, background: `${accent}18`, color: accent, fontWeight: 600, letterSpacing: 0.3 }}>
    {label}
  </span>
);

const DestCard = ({ d }) => (
  <div style={s.card} className="dest-card">
    <div style={{ ...s.cardTop, background: d.color }}>
      <div style={s.routeRow}>
        <div style={s.city}>{d.from}</div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <div style={{ ...s.arrow, color: d.accent }}>——›</div>
          <div style={{ fontSize: 10, color: "#999" }}>{d.distance}</div>
        </div>
        <div style={s.city}>{d.to}</div>
      </div>
      <div style={s.tagsRow}>
        {d.tags.map(t => <Tag key={t} label={t} accent={d.accent} />)}
        <span style={{ marginLeft: "auto", fontSize: 10, color: "#999" }}>★ {d.rating}</span>
      </div>
    </div>
    <div style={s.cardBody}>
      <p style={s.desc}>{d.desc}</p>
      <div style={s.meta}>
        <div style={s.metaItem}>
          <span style={s.metaLabel}>Durée</span>
          <span style={s.metaVal}>{d.duration}</span>
        </div>
        <div style={s.metaItem}>
          <span style={s.metaLabel}>Type</span>
          <span style={s.metaVal}>{d.type}</span>
        </div>
        <div style={s.metaItem}>
          <span style={s.metaLabel}>Départs/j</span>
          <span style={s.metaVal}>{d.trips}</span>
        </div>
      </div>
      <div style={s.cardFooter}>
        <div>
          <div style={{ fontSize: 10, color: "#999", marginBottom: 1 }}>À partir de</div>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 16, fontWeight: 600, color: "#1a1a1a" }}>
            {d.price.toLocaleString()} <span style={{ fontSize: 11, fontWeight: 400, color: "#999", fontFamily: "inherit" }}>MGA</span>
          </div>
        </div>
        <Link to={`/?from=${d.from}&to=${d.to}`} style={{ ...s.btnAccent, background: d.accent }}>
          Réserver
        </Link>
      </div>
    </div>
  </div>
);

/* ── page ── */
const Destinations = () => {
  const [query,    setQuery]    = useState("");
  const [typeFilter, setType]   = useState("Tous");
  const [cityFilter, setCity]   = useState("Toutes");
  const [sort,     setSort]     = useState("popular");

  const filtered = useMemo(() => {
    let list = DESTINATIONS.filter(d => {
      const q = query.toLowerCase();
      const matchQ = !q || d.from.toLowerCase().includes(q) || d.to.toLowerCase().includes(q);
      const matchT = typeFilter === "Tous" || d.type === typeFilter;
      const matchC = cityFilter === "Toutes" || d.from === cityFilter || d.to === cityFilter;
      return matchQ && matchT && matchC;
    });
    if (sort === "popular") list = [...list].sort((a, b) => b.trips - a.trips);
    if (sort === "price")   list = [...list].sort((a, b) => a.price - b.price);
    if (sort === "rating")  list = [...list].sort((a, b) => b.rating - a.rating);
    return list;
  }, [query, typeFilter, cityFilter, sort]);

  return (
    <div style={s.page}>
      <style>{css}</style>
      <Header />

      {/* HERO */}
      <div style={s.hero}>
        <div style={s.heroBadge}>
          <span style={s.badgeDot} />
          {DESTINATIONS.length} trajets disponibles
        </div>
        <h1 style={s.heroTitle}>Explorez <em style={{ color: "#16a34a", fontStyle: "normal" }}>Madagascar</em></h1>
        <p style={s.heroSub}>Des hauts plateaux aux côtes, trouvez votre prochain trajet en toute simplicité.</p>

        {/* SEARCH BAR */}
        <div style={s.searchBar}>
          <input
            style={s.searchInput}
            placeholder="Rechercher une ville de départ ou d'arrivée…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <div style={s.searchIcon}><SearchIcon /></div>
        </div>
      </div>

      {/* FILTERS */}
      <div style={s.filtersWrap}>
        <div style={s.filtersLeft}>
          <div style={s.filterGroup}>
            <span style={s.filterLabel}>Type</span>
            {ALL_TYPES.map(t => (
              <button key={t} style={{ ...s.pill, ...(typeFilter === t ? s.pillActive : {}) }} onClick={() => setType(t)}>{t}</button>
            ))}
          </div>
          <div style={s.filterGroup}>
            <span style={s.filterLabel}>Ville</span>
            <select style={s.select} value={cityFilter} onChange={e => setCity(e.target.value)}>
              {ALL_CITIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div style={s.sortGroup}>
          <span style={s.filterLabel}>Trier par</span>
          <select style={s.select} value={sort} onChange={e => setSort(e.target.value)}>
            <option value="popular">Popularité</option>
            <option value="price">Prix croissant</option>
            <option value="rating">Note</option>
          </select>
        </div>
      </div>

      {/* RESULTS COUNT */}
      <div style={s.count}>{filtered.length} trajet{filtered.length !== 1 ? "s" : ""} trouvé{filtered.length !== 1 ? "s" : ""}</div>

      {/* GRID */}
      <div style={s.grid} className="dest-grid">
        {filtered.length === 0 ? (
          <div style={s.empty}>Aucun trajet ne correspond à votre recherche.</div>
        ) : (
          filtered.map(d => <DestCard key={d.id} d={d} />)
        )}
      </div>

      {/* CTA BAND */}
      <div style={s.ctaBand}>
        <div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginBottom: 6, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>Vous ne trouvez pas votre trajet ?</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "white" }}>Contactez-nous, nous ajoutons de nouvelles lignes chaque semaine.</div>
        </div>
        <Link to="/contact" style={s.btnWhite}>Nous contacter</Link>
      </div>

      <footer style={s.footer}>
        <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: "#999" }}>© 2025 TransTigo · Madagascar</div>
        <div style={{ display: "flex", gap: 20 }}>
          {["Confidentialité", "Conditions", "Contact"].map(l => (
            <Link key={l} to={`/${l.toLowerCase()}`} style={{ fontSize: 12, color: "#bbb", textDecoration: "none" }}>{l}</Link>
          ))}
        </div>
      </footer>
    </div>
  );
};

/* ── icons ── */
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="6.5" cy="6.5" r="4.5" stroke="#999" strokeWidth="1.5"/>
    <path d="M10 10L14 14" stroke="#999" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

/* ── styles ── */
const s = {
  page:       { fontFamily: "'Plus Jakarta Sans',sans-serif", background: "#fafaf8", color: "#1a1a1a", minHeight: "100vh" },
  header:     { position: "sticky", top: 0, zIndex: 100, background: "rgba(250,250,248,0.92)", backdropFilter: "blur(12px)", borderBottom: "0.5px solid rgba(0,0,0,0.07)", padding: "0 48px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 60 },
  logo:       { fontSize: 18, fontWeight: 700, letterSpacing: -0.5, color: "#1a1a1a", textDecoration: "none" },
  nav:        { display: "flex", alignItems: "center", gap: 28 },
  navLink:    { fontSize: 13.5, color: "#666", textDecoration: "none" },
  btnGhost:   { fontSize: 13, padding: "7px 16px", background: "none", border: "0.5px solid #d0d0d0", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", color: "#444", textDecoration: "none" },
  btnPrimary: { fontSize: 13, padding: "7px 16px", background: "#1a1a1a", color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontWeight: 500, textDecoration: "none" },

  hero:       { padding: "64px 48px 48px", maxWidth: 800, margin: "0 auto", textAlign: "center" },
  heroBadge:  { display: "inline-flex", alignItems: "center", gap: 6, background: "#f0fdf4", border: "0.5px solid #bbf7d0", padding: "5px 14px", borderRadius: 20, fontSize: 12, color: "#16a34a", fontWeight: 500, marginBottom: 20 },
  badgeDot:   { width: 6, height: 6, background: "#16a34a", borderRadius: "50%", animation: "pulse 2s infinite", display: "inline-block" },
  heroTitle:  { fontSize: 44, fontWeight: 700, letterSpacing: -1.5, marginBottom: 14, lineHeight: 1.1 },
  heroSub:    { fontSize: 16, color: "#666", lineHeight: 1.7, marginBottom: 32 },

  searchBar:  { position: "relative", maxWidth: 540, margin: "0 auto" },
  searchInput:{ width: "100%", padding: "14px 48px 14px 18px", border: "0.5px solid #e0e0e0", borderRadius: 12, fontSize: 14, fontFamily: "inherit", background: "white", outline: "none", boxSizing: "border-box", boxShadow: "0 2px 12px rgba(0,0,0,0.05)" },
  searchIcon: { position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" },

  filtersWrap:{ maxWidth: 1200, margin: "0 auto", padding: "0 48px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 },
  filtersLeft:{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" },
  filterGroup:{ display: "flex", alignItems: "center", gap: 8 },
  filterLabel:{ fontSize: 11, color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 },
  pill:       { fontSize: 12, padding: "5px 14px", borderRadius: 20, border: "0.5px solid #e0e0e0", background: "none", cursor: "pointer", fontFamily: "inherit", color: "#666" },
  pillActive: { background: "#1a1a1a", color: "white", borderColor: "#1a1a1a" },
  select:     { fontSize: 13, padding: "6px 10px", border: "0.5px solid #e0e0e0", borderRadius: 8, fontFamily: "inherit", background: "white", color: "#444", cursor: "pointer", outline: "none" },
  sortGroup:  { display: "flex", alignItems: "center", gap: 8 },

  count:      { maxWidth: 1200, margin: "0 auto", padding: "0 48px 16px", fontSize: 13, color: "#999" },

  grid:       { maxWidth: 1200, margin: "0 auto", padding: "0 48px 64px", display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 18 },
  empty:      { gridColumn: "1/-1", textAlign: "center", padding: "60px 0", fontSize: 14, color: "#999" },

  card:       { background: "white", border: "0.5px solid #e8e8e4", borderRadius: 14, overflow: "hidden", display: "flex", flexDirection: "column" },
  cardTop:    { padding: "20px 20px 16px" },
  routeRow:   { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 },
  city:       { fontSize: 15, fontWeight: 700, letterSpacing: -0.3 },
  arrow:      { fontSize: 14, fontWeight: 500, letterSpacing: -1 },
  tagsRow:    { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
  cardBody:   { padding: "16px 20px 20px", flex: 1, display: "flex", flexDirection: "column" },
  desc:       { fontSize: 13, color: "#666", lineHeight: 1.65, marginBottom: 16, flex: 1 },
  meta:       { display: "flex", gap: 0, borderTop: "0.5px solid #f0f0f0", borderBottom: "0.5px solid #f0f0f0", padding: "10px 0", marginBottom: 16 },
  metaItem:   { flex: 1, display: "flex", flexDirection: "column", gap: 2, padding: "0 8px", borderRight: "0.5px solid #f0f0f0" },
  metaLabel:  { fontSize: 10, color: "#bbb", textTransform: "uppercase", letterSpacing: 0.4 },
  metaVal:    { fontSize: 12, fontWeight: 600, color: "#444" },
  cardFooter: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  btnAccent:  { fontSize: 12, padding: "8px 18px", borderRadius: 8, color: "white", border: "none", cursor: "pointer", fontFamily: "inherit", fontWeight: 600, textDecoration: "none", display: "inline-block" },

  ctaBand:    { background: "#1a1a1a", padding: "48px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 32 },
  btnWhite:   { padding: "12px 28px", background: "white", color: "#1a1a1a", borderRadius: 10, fontSize: 13, fontWeight: 700, textDecoration: "none", flexShrink: 0, whiteSpace: "nowrap" },
  footer:     { padding: "24px 48px", borderTop: "0.5px solid #e8e8e4", display: "flex", alignItems: "center", justifyContent: "space-between", background: "white" },
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
  @keyframes pulse { 0%,100%{opacity:1}50%{opacity:0.4} }
  .dest-card:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.07); transform: translateY(-2px); transition: all 0.2s; }
  @media(max-width:900px){ .dest-grid{ grid-template-columns:1fr!important; padding:0 24px 48px!important; } }
`;

export default Destinations;